import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authApi } from '@/api/endpoints';
import { tokenStore } from '@/store/auth';
import { Input } from '@/components/ui/Input';
import { ApiClientError } from '@/api/client';

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [firstName, setFirstName]  = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [loading, setLoading]     = useState(false);

  const referralCode = searchParams.get('ref') ?? undefined;

  const passwordMismatch = confirm.length > 0 && confirm !== password;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (!acknowledged) {
      setError('Please acknowledge how Puntsave works before continuing.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const tokens = await authApi.signup({
        email,
        password,
        firstName,
        phantomWinAcknowledged: true,
        referralCode,
      });
      tokenStore.setTokens(tokens.accessToken, tokens.refreshToken);
      // Send first-time users to the deposit page so they can fund their account.
      navigate('/deposit', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        const code = err.error.code;
        if (code === 'email_taken') {
          setError('An account with that email already exists.');
        } else if (code === 'invalid_referral_code') {
          setError('Referral code not recognised — try signing up without it.');
        } else {
          setError(err.error.message);
        }
      } else {
        setError('Network error — please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[560px] rounded-[24px] border border-white/[0.07] bg-brand-navy2 p-8 sm:p-11 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
        <div className="inline-flex items-center gap-2.5 mb-6">
          <span className="w-8 h-8 rounded-[9px] grid place-items-center bg-gradient-to-br from-brand-teal to-brand-mint text-brand-navy font-display font-extrabold text-lg">P</span>
          <span className="font-display font-extrabold tracking-[0.06em] text-[16px]">PUNTSAVE</span>
        </div>
        <h1 className="font-display font-extrabold text-3xl tracking-[-0.02em] mb-1">Create your account</h1>
        <p className="text-white/50 mb-6">Same thrill. Different result.</p>

        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
        <Input
          label="First name"
          type="text"
          autoComplete="given-name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="At least 12 characters"
          required
          minLength={12}
        />
        <Input
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={passwordMismatch ? 'Passwords do not match.' : undefined}
          required
        />
        {referralCode && (
          <p className="text-xs text-brand-teal/80">
            Referral code <strong>{referralCode}</strong> applied — you'll both earn a reward after your first deposit.
          </p>
        )}

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded accent-brand-teal flex-shrink-0"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            required
          />
          <span className="text-xs text-white/50 leading-relaxed">
            I understand that my deposited money is preserved — it redistributes between Savings
            and my Play Balance as I bet, but never disappears. A{' '}
            <a href="/how-it-works" className="underline hover:text-white/70">4% fee</a>
            {' '}applies to each bet.
          </span>
        </label>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <p className="text-xs text-white/30 leading-relaxed">
          By creating an account you agree to our{' '}
          <a href="/terms" className="underline hover:text-white/60">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy" className="underline hover:text-white/60">Privacy Policy</a>.
        </p>

        <button
          type="submit"
          disabled={loading || passwordMismatch || !acknowledged}
          className="mt-2 flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-[15px] bg-brand-green text-[#04240f] hover:brightness-95 active:scale-[0.99] disabled:opacity-50 transition"
        >
          {loading && <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
          Create account
        </button>
        <p className="text-center text-sm text-white/50">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-teal hover:brightness-110">Sign in</Link>
        </p>
        </form>
      </div>
    </div>
  );
}
